const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const MONTH_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export class FormMenuAccordion {
  constructor() {
    this.items = [...document.querySelectorAll('.l-form-menu')]
      .map((item, index) => ({
        item,
        button: item.querySelector('.l-form-menu__title'),
        list: item.querySelector('.l-form-menu__list'),
        index,
      }))
      .filter(({ button, list }) => button && list);

    if (this.items.length === 0) return;

    this.items.forEach((entry) => this.setupItem(entry));
  }

  setupItem(entry) {
    const { item, button, list, index } = entry;
    const isOpen = item.classList.contains('is-open');
    const panel = this.wrapPanel(list);

    button.type = 'button';
    entry.isAnimating = false;
    entry.panel = panel;
    entry.handleTransitionEnd = null;

    if (!panel.id) {
      panel.id = `js-form-menu-panel-${index + 1}`;
    }

    button.setAttribute('aria-controls', panel.id);
    button.setAttribute('aria-expanded', String(isOpen));
    panel.setAttribute('aria-hidden', String(!isOpen));
    panel.hidden = !isOpen;

    entry.handleClick = () => this.toggle(entry);
    button.addEventListener('click', entry.handleClick);
  }

  wrapPanel(list) {
    const parent = list.parentElement;

    if (parent && parent.classList.contains('l-form-menu__panel')) {
      return parent;
    }

    const panel = document.createElement('div');
    panel.className = 'l-form-menu__panel';
    list.before(panel);
    panel.appendChild(list);
    return panel;
  }

  toggle(entry) {
    if (entry.isAnimating) return;

    const isOpen = entry.item.classList.contains('is-open');
    isOpen ? this.close(entry) : this.open(entry);
  }

  clearTransitionHandler(entry) {
    if (!entry.handleTransitionEnd) return;

    entry.panel.removeEventListener('transitionend', entry.handleTransitionEnd);
    entry.handleTransitionEnd = null;
  }

  open(entry) {
    const { item, button, panel } = entry;
    this.clearTransitionHandler(entry);
    entry.isAnimating = true;

    panel.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      item.classList.add('is-open');
    });

    entry.handleTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'grid-template-rows') return;

      entry.isAnimating = false;
      this.clearTransitionHandler(entry);
    };

    panel.addEventListener('transitionend', entry.handleTransitionEnd);
  }

  close(entry) {
    const { item, button, panel } = entry;
    this.clearTransitionHandler(entry);
    entry.isAnimating = true;
    item.classList.remove('is-open');
    button.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');

    entry.handleTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'grid-template-rows') return;

      panel.hidden = true;
      entry.isAnimating = false;
      this.clearTransitionHandler(entry);
    };

    panel.addEventListener('transitionend', entry.handleTransitionEnd);
  }

  destroy() {
    this.items.forEach((entry) => {
      const { button, handleClick } = entry;
      this.clearTransitionHandler(entry);

      if (!button || !handleClick) return;
      button.removeEventListener('click', handleClick);
    });
  }
}

export class Form {
  constructor(root) {
    this.root = root;
    if (!this.root) return;

    this.dataEl = this.root.querySelector('.l-form-calendar__data');
    this.inner = this.root.querySelector('.l-form-calendar__inner');
    if (!this.dataEl || !this.inner) return;

    this.months = this.parseData().filter(
      (m) => m && Number.isInteger(m.year) && Number.isInteger(m.month) && m.month >= 1 && m.month <= 12,
    );
    if (this.months.length === 0) return;

    this.activeMonthIndex = 0;
    this.monthElements = [];
    this.sliderTrack = null;
    this.viewport = null;
    this.nav = null;
    this.swipeThreshold = 40;
    this.swipeSuppressUntil = 0;
    this.touchState = this.createTouchState();
    this.handlePrevClick = () => this.setActiveMonth(this.activeMonthIndex - 1);
    this.handleNextClick = () => this.setActiveMonth(this.activeMonthIndex + 1);
    this.handleCellClick = (event) => this.onCellClick(event);
    this.handleTouchStart = (event) => this.onTouchStart(event);
    this.handleTouchMove = (event) => this.onTouchMove(event);
    this.handleTouchEnd = () => this.onTouchEnd();
    this.handleTouchCancel = () => this.resetTouchState();

    this.inner.addEventListener('click', this.handleCellClick);

    this.render();
  }

  parseData() {
    try {
      const parsed = JSON.parse(this.dataEl.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  buildWeeks(year, month, closedDates) {
    const closed = Array.isArray(closedDates) ? new Set(closedDates.map(Number)) : new Set();
    const firstDay = (new Date(year, month - 1, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = Array(firstDay).fill(null);

    for (let day = 1; day <= daysInMonth; day++) {
      const status = closed.has(day) ? 'close' : 'open';
      cells.push({ day, status, muted: status === 'close' });
    }

    while (cells.length % 7 !== 0) cells.push(null);

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }

  createHeading(monthData) {
    const heading = document.createElement('div');
    heading.className = 'l-form-calendar__heading';

    const num = document.createElement('p');
    num.className = 'l-form-calendar__heading-num';
    num.textContent = String(monthData.month);

    const label = document.createElement('p');
    label.className = 'l-form-calendar__heading-label';
    label.textContent = `${MONTH_LABELS[monthData.month - 1]} ${monthData.year}`;

    heading.append(num, label);
    return heading;
  }

  createMonth(monthData, index) {
    const month = document.createElement('section');
    month.className = 'l-form-calendar__month';
    month.dataset.monthIndex = String(index);

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    WEEK_DAYS.forEach((day) => {
      const th = document.createElement('th');
      th.className = 'l-form-calendar__head';
      th.textContent = day;
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);

    const tbody = document.createElement('tbody');
    this.buildWeeks(monthData.year, monthData.month, monthData.closedDates).forEach((week) => {
      const row = document.createElement('tr');
      week.forEach((cellData) => {
        const cell = document.createElement('td');
        cell.className = 'l-form-calendar__day';
        const inner = document.createElement('button');
        inner.className = 'l-form-calendar__cell';
        inner.type = 'button';
        inner.setAttribute('aria-pressed', 'false');

        if (!cellData) {
          inner.classList.add('is-empty');
          inner.disabled = true;
          inner.setAttribute('aria-hidden', 'true');
        } else {
          const date = document.createElement('span');
          date.className = 'l-form-calendar__date';
          if (cellData.muted) date.classList.add('is-muted');
          date.textContent = String(cellData.day);
          inner.appendChild(date);

          if (cellData.status === 'open' || cellData.status === 'close') {
            const status = document.createElement('span');
            status.className = `l-form-calendar__status l-form-calendar__status--${cellData.status}`;
            status.setAttribute('aria-label', cellData.status === 'open' ? '営業日' : '定休日');
            inner.appendChild(status);
          }
        }

        cell.appendChild(inner);
        row.appendChild(cell);
      });
      tbody.appendChild(row);
    });

    const table = document.createElement('table');
    table.className = 'l-form-calendar__table';
    table.append(thead, tbody);
    month.append(this.createHeading(monthData), table);
    return month;
  }

  createTouchState() {
    return {
      startX: 0,
      startY: 0,
      deltaX: 0,
      deltaY: 0,
      isTracking: false,
    };
  }

  resetTouchState() {
    this.touchState = this.createTouchState();
  }

  onCellClick(event) {
    if (Date.now() < this.swipeSuppressUntil) {
      event.preventDefault();
      return;
    }

    const button = event.target.closest('.l-form-calendar__cell');
    if (!button || !this.inner.contains(button) || button.disabled) return;

    this.root.querySelectorAll('.l-form-calendar__cell.is-active').forEach((cell) => {
      cell.classList.remove('is-active');
      cell.setAttribute('aria-pressed', 'false');
    });

    button.classList.add('is-active');
    button.setAttribute('aria-pressed', 'true');
  }

  onTouchStart(event) {
    if (!this.viewport || this.monthElements.length <= 1 || event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchState = {
      startX: touch.clientX,
      startY: touch.clientY,
      deltaX: 0,
      deltaY: 0,
      isTracking: true,
    };
  }

  onTouchMove(event) {
    if (!this.touchState.isTracking || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchState.startX;
    const deltaY = touch.clientY - this.touchState.startY;

    this.touchState.deltaX = deltaX;
    this.touchState.deltaY = deltaY;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= 12) {
      event.preventDefault();
    }
  }

  onTouchEnd() {
    if (!this.touchState.isTracking) return;

    const { deltaX, deltaY } = this.touchState;
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= this.swipeThreshold;

    if (isHorizontalSwipe) {
      this.swipeSuppressUntil = Date.now() + 350;
      this.setActiveMonth(this.activeMonthIndex + (deltaX < 0 ? 1 : -1));
    }

    this.resetTouchState();
  }

  bindSwipeEvents() {
    if (!this.viewport || this.monthElements.length <= 1) return;

    this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.viewport.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.viewport.addEventListener('touchend', this.handleTouchEnd);
    this.viewport.addEventListener('touchcancel', this.handleTouchCancel);
  }

  unbindSwipeEvents() {
    if (!this.viewport) return;

    this.viewport.removeEventListener('touchstart', this.handleTouchStart);
    this.viewport.removeEventListener('touchmove', this.handleTouchMove);
    this.viewport.removeEventListener('touchend', this.handleTouchEnd);
    this.viewport.removeEventListener('touchcancel', this.handleTouchCancel);
    this.resetTouchState();
  }

  createNavigation() {
    const container = document.createElement('div');
    container.className = 'l-form-calendar__nav';

    const prevButton = document.createElement('button');
    prevButton.className = 'l-form-calendar__arrow l-form-calendar__arrow--prev';
    prevButton.type = 'button';
    prevButton.setAttribute('aria-label', '前の月を表示');

    const nextButton = document.createElement('button');
    nextButton.className = 'l-form-calendar__arrow l-form-calendar__arrow--next';
    nextButton.type = 'button';
    nextButton.setAttribute('aria-label', '次の月を表示');

    container.append(prevButton, nextButton);

    return {
      container,
      prevButton,
      nextButton,
    };
  }

  clampIndex(index) {
    return Math.max(0, Math.min(index, this.monthElements.length - 1));
  }

  setActiveMonth(index) {
    if (this.monthElements.length <= 1) return;
    this.activeMonthIndex = this.clampIndex(index);
    this.syncView();
  }

  syncView() {
    const hasSlider = this.monthElements.length > 1;

    if (this.sliderTrack && hasSlider) {
      this.sliderTrack.style.transform = `translateX(-${this.activeMonthIndex * 100}%)`;
    }

    this.monthElements.forEach((el, index) => {
      const isActive = !hasSlider || index === this.activeMonthIndex;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-hidden', String(!isActive));
    });

    if (this.nav) {
      this.nav.prevButton.disabled = this.activeMonthIndex <= 0;
      this.nav.nextButton.disabled = this.activeMonthIndex >= this.monthElements.length - 1;
    }
  }

  destroy() {
    if (this.nav) {
      this.nav.prevButton.removeEventListener('click', this.handlePrevClick);
      this.nav.nextButton.removeEventListener('click', this.handleNextClick);
    }

    this.unbindSwipeEvents();
    this.inner.removeEventListener('click', this.handleCellClick);
  }

  render() {
    this.unbindSwipeEvents();
    this.inner.textContent = '';
    this.root.classList.toggle('is-slider', this.months.length > 1);

    this.viewport = document.createElement('div');
    this.viewport.className = 'l-form-calendar__viewport';

    this.sliderTrack = document.createElement('div');
    this.sliderTrack.className = 'l-form-calendar__track';

    this.monthElements = this.months.map((monthData, index) => this.createMonth(monthData, index));
    this.monthElements.forEach((el) => this.sliderTrack.appendChild(el));
    this.viewport.appendChild(this.sliderTrack);
    this.inner.appendChild(this.viewport);

    if (this.months.length > 1) {
      this.nav = this.createNavigation();
      this.nav.prevButton.addEventListener('click', this.handlePrevClick);
      this.nav.nextButton.addEventListener('click', this.handleNextClick);
      this.inner.appendChild(this.nav.container);
      this.bindSwipeEvents();
    } else {
      this.nav = null;
    }

    this.syncView();
  }
}

export class FormTextareaAutoResize {
  constructor() {
    this.items = [...document.querySelectorAll('.l-form-textarea__input')]
      .map((textarea) => ({
        textarea,
        handleInput: () => this.resize(textarea),
      }));

    if (this.items.length === 0) return;

    this.handleWindowResize = () => {
      this.items.forEach(({ textarea }) => this.resize(textarea));
    };

    this.items.forEach(({ textarea, handleInput }) => {
      textarea.style.boxSizing = 'border-box';
      textarea.style.overflowY = 'hidden';
      textarea.addEventListener('input', handleInput);
      this.resize(textarea);
    });

    window.addEventListener('resize', this.handleWindowResize);
  }

  resize(textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  destroy() {
    if (!this.items || this.items.length === 0) return;

    this.items.forEach(({ textarea, handleInput }) => {
      textarea.removeEventListener('input', handleInput);
    });

    window.removeEventListener('resize', this.handleWindowResize);
  }
}
